import { Component } from '@theme/component';

/**
 * @typedef {{
 *   grid: HTMLElement;
 *   items: HTMLElement[];
 *   loadMoreButton: HTMLButtonElement | undefined;
 *   scrollSentinel: HTMLElement | undefined;
 * }} BrandGridRefs
 */

/**
 * Brand grid component with client-side load more / infinite scroll pagination.
 * All items are rendered server-side; extras are hidden with CSS and revealed in batches.
 *
 * @extends {Component<BrandGridRefs>}
 */
class BrandGridComponent extends Component {
  requiredRefs = ['grid'];

  /** @type {number} */
  #visibleCount = 0;

  /** @type {number} */
  #itemsPerPage = 20;

  /** @type {IntersectionObserver | undefined} */
  #scrollObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#itemsPerPage = parseInt(this.dataset.itemsPerPage, 10) || 20;
    this.#visibleCount = this.#countVisible();

    if (this.#visibleCount >= this.refs.items.length) {
      this.#hideControls();
      return;
    }

    if (this.dataset.paginationMode === 'infinite_scroll') {
      this.#initInfiniteScroll();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scrollObserver?.disconnect();
  }

  #initInfiniteScroll() {
    const { scrollSentinel } = this.refs;
    if (!scrollSentinel) return;

    this.#scrollObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.loadMore();
          }
        }
      },
      { rootMargin: '200px' }
    );

    this.#scrollObserver.observe(scrollSentinel);
  }

  loadMore() {
    const { items } = this.refs;
    const end = Math.min(this.#visibleCount + this.#itemsPerPage, items.length);

    for (let i = this.#visibleCount; i < end; i++) {
      items[i].classList.remove('brand-grid__item--hidden');
    }

    this.#visibleCount = end;

    if (this.#visibleCount >= items.length) {
      this.#hideControls();
      this.#scrollObserver?.disconnect();
    }
  }

  /** @returns {number} */
  #countVisible() {
    return this.refs.items.filter((item) => !item.classList.contains('brand-grid__item--hidden')).length;
  }

  #hideControls() {
    if (this.refs.loadMoreButton) {
      this.refs.loadMoreButton.hidden = true;
    }
    if (this.refs.scrollSentinel) {
      this.refs.scrollSentinel.hidden = true;
    }
  }
}

if (!customElements.get('brand-grid-component')) {
  customElements.define('brand-grid-component', BrandGridComponent);
}
